import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import theme from '../../../constants/theme'
import storageApi from '../../../services/firebaseStorage'
import UploadOutline from '../../../assets/svgs/UploadOutline'
import { Typography } from '../../styled'
import {
  DropZone,
  HiddenFileInput,
  DropIcon,
  UploadTrack,
  UploadFill,
} from '../styles'

function AttachmentInput({ value, onChange, onUploading }) {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [fileName, setFileName] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [dragActive, setDragActive] = useState(false)

  const processFile = async (file) => {
    if (!file) return

    setFileName(file.name)
    setUploading(true)
    setUploadError('')
    setProgress(0)
    onUploading?.(true)

    try {
      const blobPath = await storageApi.uploadFile(
        file,
        setProgress,
        null,
        null,
      )
      onChange(blobPath)
    } catch {
      setUploadError(t('assessment.uploadError'))
      onChange(null)
    } finally {
      setUploading(false)
      onUploading?.(false)
    }
  }

  const handleFileChange = (e) => processFile(e.target.files?.[0])

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    if (!uploading) processFile(e.dataTransfer.files?.[0])
  }

  const isUploaded = value && typeof value === 'string'

  return (
    <div className="mt-2">
      <DropZone
        dragActive={dragActive}
        uploading={uploading}
        onDragOver={(e) => {
          e.preventDefault()
          setDragActive(true)
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
      >
        <HiddenFileInput
          type="file"
          onChange={handleFileChange}
          disabled={uploading}
          accept=".pdf,.doc,.docx,.zip,.txt,.png,.jpg,.jpeg,.csv,.xlsx"
        />
        <DropIcon>
          <UploadOutline width={20} height={20} color={theme.talentinoBlue} />
        </DropIcon>
        <Typography bold size="14px" color={theme.talentinoBlack}>
          {t('assessment.clickToUpload')}
        </Typography>
        <Typography size="12px" color={theme.talentinoSecondaryGray}>
          {t('assessment.uploadHint')}
        </Typography>
      </DropZone>

      {uploading && (
        <div className="mt-2">
          <div className="d-flex align-items-center gap-2 mb-1">
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
            />
            <Typography size="12px" color="#666">
              {t('assessment.uploading')} {progress}%
            </Typography>
          </div>
          <UploadTrack>
            <UploadFill pct={progress} />
          </UploadTrack>
        </div>
      )}

      {isUploaded && !uploading && (
        <Typography size="12px" className="mt-1" color="#198754">
          {t('assessment.uploadSuccess')}{' '}
          {fileName || t('assessment.fileUploaded')}
        </Typography>
      )}

      {uploadError && (
        <Typography
          size="12px"
          className="mt-1"
          color={theme.talentinoDarkerRed || '#dc3545'}
        >
          {uploadError}
        </Typography>
      )}
    </div>
  )
}

AttachmentInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onUploading: PropTypes.func,
}

export default AttachmentInput
